import type { DataSourceDataParams } from "@infinite-table/infinite-react";
import { type ClassValue, clsx } from "clsx";
import type {
	FilterCondition,
	FilterParams,
	LookupParams,
	MetadataTypes,
	PaginationParams,
} from "epsm-api-types";
import { Converter, type IConverter } from "epsm-api-types/converter";
import type { ColumnType } from "epsm-api-types/models";
import { twMerge } from "tailwind-merge";
import type { IReadAllParams } from "@/services/RequestParams";

const converter = new Converter();

export const ONE_YEAR_IN_MS = 31536000000;
export const ONE_YEAR_IN_S = ONE_YEAR_IN_MS / 1000;
export const ONE_DAY_IN_MS = 86400000;
export const ONE_DAY_IN_S = ONE_DAY_IN_MS / 1000;
export const ONE_MONTH_IN_MS = 31 * ONE_DAY_IN_MS;
export const ONE_HOUR_IN_MS = 3600000;
export const ONE_MIN_IN_MS = 60000;

export const getCurrentTimeInMs = (): number => Date.now();

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const compareStrings = (str1?: string, str2?: string): number => {
	const s1 = str1 ?? "";
	const s2 = str2 ?? "";
	return s1.localeCompare(s2, undefined, { sensitivity: "base" });
};

export const escapeRegex = (str: string): string => {
	return str.replace(/[/\-\\^$*+?.()|[\]{}]/g, "\\$&");
};

export const mapFilterOperator = ({
	operator,
	value,
}: {
	operator: string;
	type: string;
	value: any;
}): {
	condition: FilterCondition;
	value: unknown;
	options?: string;
} => {
	switch (operator) {
		case "includes":
			return { condition: "regex", value: escapeRegex(value), options: "i" };

		case "startsWith":
			return {
				condition: "regex",
				value: `^${escapeRegex(value)}`,
				options: "i",
			};

		case "endsWith":
			return {
				condition: "regex",
				value: `${escapeRegex(value)}$`,
				options: "i",
			};

		case "neq":
			return { condition: "ne", value };

		case "eq":
		case "gt":
		case "gte":
		case "lt":
		case "lte":
			return { condition: operator, value };
	}

	return { condition: "eq", value };
};

export const getParams = <T>(
	{
		sortInfo,
		lazyLoadBatchSize,
		lazyLoadStartIndex,
		livePaginationCursor,
		filterValue,
	}: DataSourceDataParams<T>,
	fields?: string[],
	pageSize?: number,
	filters?: FilterParams,
	lookup?: LookupParams,
): IReadAllParams => {
	const sort = sortInfo
		? Array.isArray(sortInfo)
			? sortInfo
			: [sortInfo]
		: [];

	const filtersFinal: FilterParams = [
		...(filters ?? []),
		...(filterValue?.flatMap(({ field, filter }) =>
			filter?.value != null
				? [{ name: field as string, ...mapFilterOperator(filter) }]
				: [],
		) ?? []),
	];

	let pagination: PaginationParams | undefined;

	if (lazyLoadBatchSize) {
		pagination = {
			limit: lazyLoadBatchSize,
			offset: lazyLoadStartIndex ?? 0,
		};
	} else if (pageSize) {
		pagination = {
			limit: pageSize,
			offset: (livePaginationCursor as number) ?? 0,
		};
	}

	const params: IReadAllParams = {
		...(pagination && { pagination }),
		...(lazyLoadStartIndex === 0 && { total: "include" }),
		...(sort.length && {
			sort: sort.map((item) => ({
				name: item.field as string,
				order: item.dir,
			})),
		}),
		...(fields && { fields }),
		filter: filtersFinal,
		lookup,
	};

	return params;
};

export const getUnixTime = (ISODate: string) => {
	const [year, month, day] = ISODate.split("T")[0].split("-").map(Number);
	return Date.UTC(year, month - 1, day);
};

export const getISODate = (value: unknown) => {
	return (converter.convert(value, "date") as Date).toISOString();
};

export const getAlign = (type: ColumnType): "start" | "center" | "end" => {
	switch (type) {
		case "num":
		case "date":
			return "end";
		case "bool":
			return "center";
	}
	return "start";
};

export const prepareData = <TItem, TItemDTO, TResult = TItem>(
	converter: IConverter<TItem, TItemDTO>,
	data?: TItemDTO[],
	types?: MetadataTypes,
	mapper?: (item: TItem) => TResult,
): TResult[] => {
	if (!data) return [];
	const result = converter.itemsFromDTO(data, types);
	return mapper ? result.map(mapper) : (result as unknown as TResult[]);
};

export const prepareItem = <TItem, TItemDTO, TResult = TItem>(
	converter: IConverter<TItem, TItemDTO>,
	item: TItemDTO,
	types?: MetadataTypes,
	mapper?: (item: TItem) => TResult,
): TResult => {
	if (!item) return item as unknown as TResult;
	const result = converter.fromDTO(item, types);
	return mapper ? mapper(result) : (result as unknown as TResult);
};

export const shiftDateToLocalTimeZone = (timestamp: number | Date): number => {
	const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
	return Date.UTC(
		d.getFullYear(),
		d.getMonth(),
		d.getDate(),
		d.getHours(),
		d.getMinutes(),
		d.getSeconds(),
		d.getMilliseconds(),
	);
};
